import { useState } from 'react';
import { Box, Divider, Paper, Typography } from '@mui/material';
import { CheckCircle, InsertDriveFileOutlined } from '@mui/icons-material';
import { makeStyles } from 'tss-react/mui';
import { Formik, setNestedObjectValues } from 'formik';
import { License, uploadFileToS3, UploadPurpose } from '@uplift/api';
import { useCreateProviderLicenseCollabAgreementMutation } from '@uplift/queries';
import { CalendarDateService } from '@uplift/services';
import { DateFormats } from '@uplift/constants';
import {
  Alert,
  Button,
  createCollabAgreementDefaultValues,
  CreateCollabAgreementForm,
  CreateCollabAgreementFormValues,
  createCollabAgreementValidationSchema,
  getDefaultDate,
  useAlert,
} from '@uplift/ui-v2';

const useStyles = makeStyles()(theme => ({
  root: {
    maxWidth: 700,
    width: '100%',
    margin: '0 auto',
    padding: theme.spacing(6, 0),
  },
  title: {
    marginBottom: theme.spacing(1),
  },
  subtitle: {
    marginBottom: theme.spacing(4),
  },
  overline: {
    display: 'block',
    color: theme.palette.primary.main,
    fontWeight: 700,
    letterSpacing: 1,
  },
  summary: {
    marginBottom: theme.spacing(4),
  },
  summaryRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2, 3),
  },
  reviewCard: {
    display: 'flex',
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
  },
  reviewBody: {
    flex: 1,
  },
  fileChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(0.75, 1.5),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.grey[100],
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(2),
  },
}));

export type CollabAgreementStepProps = {
  license: Pick<License, 'id' | 'userId' | 'stateId'>;
  stateName: string;
  onChangePracticeType?: () => void;
  onBack: () => void;
  onComplete: () => void;
};

export const CollabAgreementStep = ({ license, stateName, onChangePracticeType, onBack, onComplete }: CollabAgreementStepProps) => {
  const { classes } = useStyles();
  const alert = useAlert();
  const [ substep, setSubstep ] = useState<'form' | 'review'>('form');
  const createMutation = useCreateProviderLicenseCollabAgreementMutation(license.userId, license.id);

  const onSubmit = async (values: CreateCollabAgreementFormValues) => {
    if (!values.collaborationAgreement) {
      return;
    }

    const uploadedReq = await uploadFileToS3({
      providerId: license.userId,
      purpose: UploadPurpose.collaborationAgreement,
      file: values.collaborationAgreement,
    });

    await createMutation.mutateAsync({
      providerId: license.userId,
      licenseId: license.id,
      data: {
        fileUploadId: uploadedReq.file_upload.id,
        ...values,
        expirationDate: values.noExpiration ? null : getDefaultDate(values.expirationDate),
      },
    });

    alert.success('Collaborative agreement submitted for review');
    onComplete();
  };

  return (
    <Formik
      initialValues={createCollabAgreementDefaultValues}
      validationSchema={createCollabAgreementValidationSchema}
      onSubmit={onSubmit}
    >
      {formik => {
        const goToForm = () => setSubstep('form');

        const handleContinue = async () => {
          const errors = await formik.validateForm();

          if (Object.keys(errors).length === 0) {
            setSubstep('review');
          } else {
            formik.setTouched(setNestedObjectValues(errors, true));
          }
        };

        if (substep === 'review') {
          const physicianName = `${formik.values.physicianFirstName} ${formik.values.physicianLastName}`.trim();
          const expirationLabel = formik.values.noExpiration
            ? 'No expiration'
            : `Expires ${CalendarDateService.format(formik.values.expirationDate, DateFormats['MMMM D, YYYY'])}`;

          return (
            <Box className={classes.root}>
              <Box display="flex" flexDirection="column" gap="14px">
                <Typography sx={{ fontSize: '24px', fontWeight: 600 }}>
                  Review &amp; go live
                </Typography>
                <Typography color="text.secondary">
                  We&apos;ll review your collaborative agreement before you go live in {stateName}.
                </Typography>

                <Typography variant="overline" className={classes.overline}>
                  WHAT YOU&apos;RE SUBMITTING
                </Typography>

                <Paper variant="outlined" className={classes.reviewCard}>
                  <CheckCircle color="success" />
                  <Box className={classes.reviewBody}>
                    <Typography fontWeight={700}>Collaborative Practice Agreement</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {physicianName} &middot; NPI {formik.values.physicianNpi} &middot; {expirationLabel}
                    </Typography>
                    {formik.values.collaborationAgreement && (
                      <Box className={classes.fileChip}>
                        <InsertDriveFileOutlined fontSize="small" color="action" />
                        <Typography variant="body2">{formik.values.collaborationAgreement.name}</Typography>
                      </Box>
                    )}
                  </Box>
                  <Button variant="text" color="primary" onClick={goToForm} disableRipple sx={{ '&:hover': { backgroundColor: 'transparent' } }}>Edit</Button>
                </Paper>

                <Alert severity="info" showIcon={false}>
                  After you submit, UpLift typically reviews within 2 business days. We&apos;ll email you when you&apos;re cleared to see patients in {stateName}.
                </Alert>

                <Box className={classes.actions}>
                  <Button variant="contained" color="primary" disabled={formik.isSubmitting} onClick={formik.submitForm}>
                    Submit for review
                  </Button>
                  <Button variant="outlined" disabled={formik.isSubmitting} onClick={goToForm}>
                    Back
                  </Button>
                </Box>
              </Box>
            </Box>
          );
        }

        return (
          <Box className={classes.root}>
            <Typography sx={{ fontSize: '24px', fontWeight: 600 }} className={classes.title}>
              Your {stateName} setup
            </Typography>
            <Typography color="text.secondary" className={classes.subtitle}>
              Here&apos;s your path based on your answers. Change anything below, then continue.
            </Typography>

            <Paper variant="outlined" className={classes.summary}>
              <Box className={classes.summaryRow}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Practice type</Typography>
                  <Typography>Collaborative agreement</Typography>
                </Box>
                {onChangePracticeType && (
                  <Button variant="text" color="primary" onClick={onChangePracticeType}>Change</Button>
                )}
              </Box>
              <Divider />
              <Box className={classes.summaryRow}>
                <Box>
                  <Typography variant="body2" color="text.secondary">How you&apos;ll meet it</Typography>
                  <Typography>Upload your signed agreement</Typography>
                </Box>
                <Button variant="text" color="primary" onClick={onBack}>Change</Button>
              </Box>
            </Paper>

            <form onSubmit={formik.handleSubmit}>
              <CreateCollabAgreementForm />
            </form>

            <Box className={classes.actions} sx={{ mt: 4 }}>
              <Button variant="contained" color="primary" disabled={formik.isSubmitting} onClick={handleContinue}>
                Continue to review
              </Button>
              <Button variant="outlined" disabled={formik.isSubmitting} onClick={onBack}>
                Back
              </Button>
            </Box>
          </Box>
        );
      }}
    </Formik>
  );
};
